/**
 * @fileoverview Anotações do ticket — compose area + timeline de notas existentes.
 * Paleta alinhada ao modal Gerenciar Etiquetas (radius 16/18, CTA #4f7cff → #3b82f6).
 * A nota-resumo da triagem muda sozinha: o aviso da triagem desta conversa
 * (socket da empresa) relê a lista.
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import { formatDistanceToNow, format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { ClipboardCheck, Loader2, StickyNote, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import ConfirmationModal from "../ConfirmationModal";
import useTicketNotes from "../../hooks/useTicketNotes";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import usePermission from "../../hooks/usePermission";

const useStyles = makeStyles((theme) => {
	const dark = theme.palette.type === "dark";
	const textPrimary = dark ? "#f8fafc" : "#111827";
	const textSecondary = dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280";
	const textMuted = dark ? "rgba(248, 250, 252, 0.5)" : "#9ca3af";
	const border = dark
		? "1px solid rgba(255, 255, 255, 0.12)"
		: "1px solid rgba(15, 23, 42, 0.12)";
	const borderSubtle = dark
		? "1px solid rgba(255, 255, 255, 0.06)"
		: "1px solid rgba(15, 23, 42, 0.08)";
	const bgInput = dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)";
	const bgInputFocus = dark ? "rgba(255, 255, 255, 0.05)" : "#ffffff";
	const bgCard = dark
		? "rgba(255, 255, 255, 0.02)"
		: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)";
	const bgCardHover = dark ? "rgba(255, 255, 255, 0.04)" : "rgba(59, 130, 246, 0.04)";
	const t = "all 180ms cubic-bezier(0.4, 0, 0.2, 1)";

	return {
		root: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
		sectionLabel: {
			fontSize: 10,
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.14em",
			color: textMuted,
			marginBottom: 8,
		},
		composer: { display: "flex", flexDirection: "column", gap: 10 },
		textarea: {
			width: "100%",
			padding: theme.spacing(1.4, 1.8),
			border,
			background: bgInput,
			borderRadius: 16,
			color: textPrimary,
			fontSize: "0.98rem",
			lineHeight: 1.5,
			fontFamily: "inherit",
			resize: "vertical",
			minHeight: 84,
			outline: "none",
			transition: t,
			boxSizing: "border-box",
			"&::placeholder": {
				color: dark ? "rgba(248, 250, 252, 0.55)" : "#9ca3af",
				opacity: 1,
			},
			"&:focus": {
				borderColor: dark ? "#7aa2ff" : "#3b82f6",
				background: bgInputFocus,
				boxShadow: dark
					? "0 0 0 1px rgba(122, 162, 255, 0.15)"
					: "0 0 0 1px rgba(59, 130, 246, 0.12)",
			},
		},
		actions: { display: "flex", justifyContent: "flex-end", gap: 8 },
		btnSave: {
			minWidth: 110,
			height: 36,
			padding: "0 18px",
			borderRadius: 10,
			border: "none",
			background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
			color: "#ffffff",
			fontSize: "0.95rem",
			fontWeight: 500,
			cursor: "pointer",
			transition: t,
			fontFamily: "inherit",
			textTransform: "none",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			gap: 8,
			boxShadow: "none",
			"&:hover:not(:disabled)": {
				background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
			},
			"&:disabled": { opacity: 0.45, cursor: "not-allowed" },
		},
		btnGhost: {
			height: 36,
			padding: "0 16px",
			borderRadius: 10,
			border,
			background: "transparent",
			color: textSecondary,
			fontSize: "0.95rem",
			fontWeight: 500,
			cursor: "pointer",
			transition: t,
			fontFamily: "inherit",
			textTransform: "none",
			"&:hover": {
				background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
				color: textPrimary,
			},
		},
		list: { display: "flex", flexDirection: "column", gap: 10 },
		note: {
			display: "flex",
			gap: 12,
			padding: theme.spacing(1.5, 2),
			borderRadius: 18,
			border: borderSubtle,
			background: bgCard,
			position: "relative",
			transition: t,
			"&:hover": { background: bgCardHover },
			"&:hover $deleteBtn": { opacity: 1 },
		},
		noteAvatar: {
			width: 32,
			height: 32,
			fontSize: 12,
			fontWeight: 700,
			background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
			color: "#ffffff",
			flexShrink: 0,
		},
		noteBody: { flex: 1, minWidth: 0 },
		noteHeader: {
			display: "flex",
			alignItems: "baseline",
			gap: 6,
			marginBottom: 4,
		},
		noteAuthor: {
			fontSize: "0.92rem",
			fontWeight: 700,
			color: textPrimary,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		noteDate: {
			fontSize: "0.75rem",
			color: textMuted,
			flexShrink: 0,
			fontWeight: 500,
		},
		noteText: {
			fontSize: "0.94rem",
			color: textPrimary,
			lineHeight: 1.5,
			whiteSpace: "pre-wrap",
			wordBreak: "break-word",
		},
		// Nota-resumo da triagem: escrita pelo sistema, só leitura, no topo da lista.
		// Depois das classes de base: na mesma especificidade, vale a regra que vem depois.
		noteAvatarSummary: {
			width: 32,
			height: 32,
			background: dark ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
			color: textSecondary,
			flexShrink: 0,
		},
		// Cabeçalho da nota-resumo: o autor não encolhe; a data, sim.
		noteAuthorFixed: {
			flexShrink: 0,
		},
		noteDateFlexible: {
			flexShrink: 1,
			minWidth: 0,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		noteTag: {
			fontSize: "0.72rem",
			fontWeight: 600,
			color: textMuted,
			marginBottom: 4,
		},
		noteTextSummary: {
			fontSize: "0.86rem",
			lineHeight: 1.55,
		},
		deleteBtn: {
			position: "absolute",
			top: 8,
			right: 8,
			opacity: 0,
			background: "transparent",
			border: "none",
			color: textMuted,
			cursor: "pointer",
			padding: 6,
			borderRadius: 10,
			display: "inline-flex",
			transition: t,
			"&:hover": {
				color: "#ef4444",
				background: dark ? "rgba(239, 68, 68, 0.12)" : "rgba(239, 68, 68, 0.08)",
			},
		},
		empty: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 10,
			padding: theme.spacing(3),
			borderRadius: 18,
			border: borderSubtle,
			background: bgCard,
			color: textMuted,
		},
		emptyText: { fontSize: "0.94rem", fontWeight: 500 },
		spin: { animation: "$spin 0.8s linear infinite" },
		"@keyframes spin": {
			"0%": { transform: "rotate(0deg)" },
			"100%": { transform: "rotate(360deg)" },
		},
	};
});

const formatRelative = (iso) => {
	try {
		return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
	} catch {
		return format(new Date(iso), "dd/MM/yy HH:mm", { locale: ptBR });
	}
};

// Nota-resumo da triagem (kind "triage_summary"): o sistema escreve e atualiza.
const RESUMO_DA_TRIAGEM = "triage_summary";

const initials = (name) => {
	if (!name) return "?";
	const parts = name.trim().split(/\s+/);
	return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || name[0].toUpperCase();
};

export function ContactNotes({ ticket }) {
	const classes = useStyles();
	const { id: ticketId, contactId } = ticket || {};
	const { saveNote, deleteNote, listNotes } = useTicketNotes();

	const { user } = useContext(AuthContext);
	const socketManager = useContext(SocketContext);
	// Apagar anotação (qualquer uma, até a própria) pede notes.manage_others: hoje,
	// o perfil admin, como a API (a lixeira para os demais só dava erro).
	const { can } = usePermission();
	const canDeleteNotes = can("notes.manage_others", user?.profile === "admin");
	const companyId = user?.companyId;

	const [notes, setNotes] = useState([]);
	const [draft, setDraft] = useState("");
	const [saving, setSaving] = useState(false);
	const [deletingNote, setDeletingNote] = useState(null);
	// Conversa mostrada agora: resposta pedida por outra (depois de trocar) é ignorada.
	const ticketRef = useRef(ticketId);
	ticketRef.current = ticketId;

	// silencioso: releitura pelo aviso da triagem, sem mensagem de erro na tela.
	const loadNotes = async (silencioso = false) => {
		if (!ticketId) return;
		const pedido = ticketId;
		try {
			const fetched = await listNotes({ ticketId, contactId });
			if (ticketRef.current !== pedido) return;
			setNotes(fetched || []);
		} catch (e) {
			if (ticketRef.current !== pedido || silencioso) return;
			toast.error(String(e));
		}
	};

	useEffect(() => {
		loadNotes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketId, contactId]);

	useEffect(() => {
		if (!ticketId || !companyId) return undefined;
		const socket = socketManager.getSocket(companyId);
		let timer = null;
		const onTriage = (data) => {
			if (Number(data?.ticketId) !== Number(ticketId)) return;
			// Várias mudanças seguidas viram uma leitura só.
			clearTimeout(timer);
			timer = setTimeout(() => loadNotes(true), 500);
		};
		socket.on(`company-${companyId}-triage`, onTriage);
		return () => {
			clearTimeout(timer);
			// disconnect() do socket gerenciado só solta os ouvintes deste uso (a conexão continua).
			socket.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketId, contactId, companyId, socketManager]);

	const handleSave = async () => {
		const note = draft.trim();
		if (note.length < 2) return;
		setSaving(true);
		try {
			await saveNote({ note, ticketId, contactId });
			setDraft("");
			await loadNotes();
			toast.success("Anotação adicionada");
		} catch (e) {
			toast.error(String(e));
		} finally {
			setSaving(false);
		}
	};

	const handleKeyDown = (e) => {
		if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
			e.preventDefault();
			handleSave();
		}
	};

	const handleConfirmDelete = async () => {
		if (!deletingNote) return;
		try {
			await deleteNote(deletingNote.id);
			setDeletingNote(null);
			await loadNotes();
			toast.success("Anotação excluída");
		} catch (e) {
			toast.error(String(e));
		}
	};

	const canSave = draft.trim().length >= 2 && !saving;
	// Nota-resumo da triagem primeiro; as demais na ordem de sempre (mais nova antes).
	const ordenadas = [...notes].sort(
		(a, b) => Number(b.kind === RESUMO_DA_TRIAGEM) - Number(a.kind === RESUMO_DA_TRIAGEM)
	);

	return (
		<div className={classes.root}>
			<ConfirmationModal
				title="Excluir anotação"
				open={!!deletingNote}
				onClose={() => setDeletingNote(null)}
				onConfirm={handleConfirmDelete}
			>
				Deseja realmente excluir esta anotação?
			</ConfirmationModal>

			<div className={classes.composer}>
				<div className={classes.sectionLabel}>Nova anotação</div>
				<textarea
					className={classes.textarea}
					placeholder="Escreva uma anotação interna sobre este contato..."
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={handleKeyDown}
					rows={3}
				/>
				<div className={classes.actions}>
					{draft && (
						<button type="button" className={classes.btnGhost} onClick={() => setDraft("")}>
							Cancelar
						</button>
					)}
					<button
						type="button"
						className={classes.btnSave}
						disabled={!canSave}
						onClick={handleSave}
					>
						{saving && <Loader2 size={14} strokeWidth={2} className={classes.spin} />}
						Salvar
					</button>
				</div>
			</div>

			<div>
				<div className={classes.sectionLabel}>
					Registradas {notes.length > 0 && `(${notes.length})`}
				</div>
				{notes.length === 0 ? (
					<div className={classes.empty}>
						<StickyNote size={30} strokeWidth={1.4} />
						<span className={classes.emptyText}>Nenhuma anotação ainda</span>
					</div>
				) : (
					<div className={classes.list}>
						{ordenadas.map((note) => {
							const resumo = note.kind === RESUMO_DA_TRIAGEM;
							return (
								<div key={note.id} className={classes.note} data-note-kind={note.kind || "manual"}>
									{resumo ? (
										<Avatar className={classes.noteAvatarSummary}>
											<ClipboardCheck size={16} strokeWidth={1.75} />
										</Avatar>
									) : (
										<Avatar className={classes.noteAvatar}>{initials(note.user?.name)}</Avatar>
									)}
									<div className={classes.noteBody}>
										<div className={classes.noteHeader}>
											<span className={`${classes.noteAuthor} ${resumo ? classes.noteAuthorFixed : ""}`}>
												{resumo ? "IA · Triagem" : note.user?.name || "—"}
											</span>
											<span className={`${classes.noteDate} ${resumo ? classes.noteDateFlexible : ""}`}>
												· {formatRelative(resumo ? note.updatedAt || note.createdAt : note.createdAt)}
											</span>
										</div>
										{resumo && <div className={classes.noteTag}>Resumo da triagem · atualizado pelo sistema</div>}
										<div className={`${classes.noteText} ${resumo ? classes.noteTextSummary : ""}`}>{note.note}</div>
									</div>
									{!resumo && canDeleteNotes && (
										<button
											type="button"
											className={classes.deleteBtn}
											onClick={() => setDeletingNote(note)}
											aria-label="Excluir anotação"
										>
											<Trash2 size={14} strokeWidth={1.75} />
										</button>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
